import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { SESSION_STORAGE, StorageService, User } from '@phdsoft/common';
import { ChangesModel } from 'projects/phdsoft/common/src/lib/models/changes.model';
import { Observable, finalize, forkJoin, map, switchMap, tap } from 'rxjs';
import { ConsequenceService } from 'src/app/admin/pages/risk-setup/service/consequence.service';
import { ConsequenceItem } from 'src/app/admin/pages/risk-setup/types/risk-config.model';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { AssetManagerLoaderService } from 'src/app/asset-manager/asset-manager-loader.service';
import { DTSelectionService } from 'src/app/digital-twin/selection/selection.service';
import { ThreedService } from 'src/app/shared/services/threed.service';
import { ConsequenceMapElement, ConsequenceMapElementDTO, ConsequenceMapRegion, ConsequenceMapRegionDTO, ConsequenceMapSection, ConsequenceMapSectionDTO, SectionOnlyViewItem, MultiConsequenceDetail, ConsequenceAnalysis, toConsequenceMapElementDTO, toConsequenceMapRegionDTO, toConsequenceMapSectionDTO } from './types/consequence-map.interface';
import { ConsequenceMapService } from './service/consequence-map.service';
import { ISideboard } from '@app/side-board/ISideboard';
import { TemplateService } from '@app/template/template.service';

@Component({
  selector: 'phdsoft-consequence-map',
  templateUrl: './consequence-map.component.html',
  styleUrls: ['./consequence-map.component.scss']
})
export class ConsequenceMapComponent implements OnInit {
  constructor(
    private threeDService: ThreedService,
    private assetManager: AssetManager,
    private consequenceService: ConsequenceService,
    private storageService: StorageService,
    private assetManagerLoaderService: AssetManagerLoaderService,
    private selectionService: DTSelectionService,
    private consequenceMapService: ConsequenceMapService,
    private templateService: TemplateService,
    private cdr: ChangeDetectorRef
  ) {
    this.assetId = this.assetManager.asset.assetInfo.AssetId
  }

  async ngOnInit() {
    this.user = await this.storageService.getItem(SESSION_STORAGE.USER)
    this.customerId = this.assetManager.asset.assetInfo.CustomerId
    this.isLoadingComponentData = true
    this.fetchConsequenceItems(this.customerId)
    .pipe(
      switchMap(() => this.fetchAllRegionsAndSections()),
      switchMap((regionsAndSections) => {
        this.regions = regionsAndSections
        return forkJoin({
          regions: this.consequenceMapService.getConsequenceMapRegions(this.assetId),
          sections: this.consequenceMapService.getConsequenceMapSections(this.assetId),
          elements: this.consequenceMapService.getConsequenceMapElements(this.assetId)
        })
      }),
      finalize(() => {
        this.isLoadingComponentData = false
        // this.buildSectionOnlyView()
      })
    ).subscribe({
      next: (consequenceMap) => {
        this.mergeConsequenceMapRegionsAndSectionsFromDatabase(consequenceMap.regions, consequenceMap.sections)
        this.fillTrackedElementsStore(consequenceMap.elements)
        // this.buildSectionOnlyView()
        // Initialize filtered arrays
        this.updateFilteredData()
        console.log(consequenceMap)
      },
      error: (error) => {
        console.log("error fetching data: ", error)
      }
    })
  }

  fetchConsequenceItems(customerId: string) {
    return this.consequenceService.getConsequenceItems(customerId).pipe(
      tap((consequenceItems) => {
        this.consequenceItems = [...consequenceItems].sort((a, b) => a.order - b.order)
        console.log(consequenceItems)
      })
    )
  }

  fetchAllRegionsAndSections() {
    return this.getAllRegionsAndSections().pipe(
      tap((regionsAndSections) => {
        this.regions = regionsAndSections
      })
    )
  }

  mergeConsequenceMapRegionsAndSectionsFromDatabase(db_regions: ConsequenceMapRegionDTO[], db_sections: ConsequenceMapSectionDTO[]) {
    this.regions.forEach(region => {
      const dbRegion = db_regions.find(x => x.regionId === region.regionId)
      if(dbRegion){
        region.consequenceId = dbRegion.consequenceId
        region.id = dbRegion.id // Copy the database ID so we can delete it later if needed
      }

      region.sections.forEach(section => {
        const dbSection = db_sections.find(x => x.sectionId === section.sectionId && x.regionId === section.regionId)
        if(dbSection) {
          section.consequenceId = dbSection.consequenceId
          section.id = dbSection.id // Copy the database ID so we can delete it later if needed
        }
      })
    })
  }

  consequenceItems: ConsequenceItem[]
  trackedElements: ConsequenceMapElement[] = []

  defaultRegionSelectValue = "DEFAULT_REGION"
  defaultSectionSelectValue = "DEFAULT_SECTION"
  defaultElementSelectValue = "DEFAULT_ELEMENT"

  currentSelectedItem: any

  user: User
  customerId: string

  assetId: string

  isLoadingComponentData: boolean = false
  isLoadingStructure: boolean = false

  regions: ConsequenceMapRegion[] = []

  // Section mode properties
  isSectionMode: boolean = false
  sectionOnlyView: SectionOnlyViewItem[] = []
  expandedSectionId: string | null = null // Track which section is expanded

  // Search functionality properties
  searchTerm: string = ''
  filteredRegions: ConsequenceMapRegion[] = []
  filteredSectionOnlyView: SectionOnlyViewItem[] = []

  optionsWindow: ISideboard = {
    VisibleMini: true,
    VisibleClose: true,
    isMinimized: false,
    Header: {
      VisibleTitle: true,
      Title: 'Consequence Map',
      Visible: true,
    },
    Footer: {
      ShowCancel: false,
      ShowOk: false,
      Visible: true,
    },
  }

  handleClickRegion(region: ConsequenceMapRegion): void {
    if (!this.isLoadingStructure) {
      this.toggleRegionVisibility(region);
      this.onRegionSelect(region);
      this.logData(region);
    }
  }

  handleClickSection(region: ConsequenceMapRegion, section: ConsequenceMapSection): void {
    if (!this.isLoadingStructure) {
      this.toggleSectionVisibility(region, section);
      this.onSectionSelect(region, section);
      this.logData(section)
    }
  }

  handleClickElement(region: ConsequenceMapRegion, section: ConsequenceMapSection, element: ConsequenceMapElement) : void {
    if (!this.isLoadingStructure) {
      this.onElementSelect(region, section, element);
      this.logData(element)
    }
  }

  toggleRegionVisibility(region: any) {
    region.visible = !region.visible;

    if (region.visible) {
      this.regions.forEach(r => {
        if (r !== region) {
          r.visible = false;
        }
        r.sections.forEach(s => s.visible = false);
      });
    }
  }

  toggleSectionVisibility(region: any, section: any) {
    section.visible = !section.visible;

    if (section.visible) {
      region.sections.forEach(s => {
        if (s !== section) {
          s.visible = false;
        }
      });
    }
  }

  logData(item: any) {
    console.log(item)
  }

  getAllRegionsAndSections(): Observable<ConsequenceMapRegion[]> {
    return forkJoin([
      this.threeDService.getRegions(this.assetId),
      this.threeDService.getSections(this.assetId),
    ]).pipe(
      map(([regions, sections]) => {
        const consequenceMapRegions: ConsequenceMapRegion[] = regions.map((region): ConsequenceMapRegion => {
          const consequenceMapSections: ConsequenceMapSection[] = sections
          .filter((section) => section.regions.includes(region.id))
          .map((section): ConsequenceMapSection => {
            return {
              regionId: region.id,
              sectionId: section.id,
              name: section.name,
              assetId: section.assetId,
              consequenceId: this.defaultSectionSelectValue,
              visible: false,
              elements: []
            }
          });

          return {
            regionId: region.id,
            name: region.name,
            assetId: region.assetId,
            consequenceId: this.defaultRegionSelectValue,
            visible: false,
            sections: consequenceMapSections
          }
        });
        return consequenceMapRegions;
      })
    );
  }

  async onRegionSelect(region: ConsequenceMapRegion){
    if(this.isLoadingStructure){
      return;
    }
    this.currentSelectedItem = region

    let currentItem: ChangesModel = {
      assetId: this.assetId,
      regionId: region.regionId
    }

    this.isLoadingStructure = true
    await this.assetManagerLoaderService.verifyRegionAndSection(currentItem)
    this.isLoadingStructure = false

    this.assetManager.asset.mode = 'REGIONS'
    this.assetManagerLoaderService.highlightRegion(currentItem)
  }

  async onSectionSelect(region: ConsequenceMapRegion, section: ConsequenceMapSection){
    if(this.isLoadingStructure){
      return;
    }

    this.loadSectionElements(section.sectionId, region.regionId)

    this.currentSelectedItem = section

    let currentItem: ChangesModel = {
      assetId: this.assetId,
      regionId: region.regionId,
      sectionId: section.sectionId
    }

    await this.assetManagerLoaderService.verifyRegionAndSection(currentItem)

    this.selectionService.setSectionSelectionFromListBox(this.assetManager.asset, region.regionId, section.sectionId)
    this.assetManagerLoaderService.highlightSection(currentItem)
  }

  async onElementSelect(region: ConsequenceMapRegion, section: ConsequenceMapSection, element: ConsequenceMapElement) {
    if(this.isLoadingStructure) {
      return
    }

    this.currentSelectedItem = element

    this.selectionService.selectElement(region.regionId, section.sectionId, element.elementId)
  }

  async loadSectionElements(sectionId: string, regionId: string) {
    if(!sectionId || !regionId) {
      return;
    }

    let sectionElements = this.assetManager.asset.StructureCampaigns[0].Elements.filter(x => x.SectionId == sectionId && x.regionId == regionId)
    if(sectionElements.length == 0) {
      return;
    }

    let consequenceMapElements: ConsequenceMapElement[] = []

    for (let i = 0; i < sectionElements.length; i++) {
      let element: ConsequenceMapElement = {
        elementId: sectionElements[i].Guid,
        name: sectionElements[i].properties.General.Label,
        assetId: this.assetId,
        regionId: sectionElements[i].regionId,
        sectionId: sectionElements[i].SectionId,
        consequenceId: this.defaultElementSelectValue,
        visible: false
      }

      consequenceMapElements.push(element)
    }

    this.regions.find(x => x.regionId == regionId).sections.find(x => x.sectionId == sectionId).elements = consequenceMapElements

    let loadedElements = this.regions.find(x => x.regionId === regionId).sections.find(x => x.sectionId === sectionId).elements;

    loadedElements.forEach(element => {
      const trackedElement = this.trackedElements.find(el => el.elementId === element.elementId && el.sectionId == element.sectionId && el.regionId == element.regionId);
      if (trackedElement) {
          element.consequenceId = trackedElement.consequenceId;
          element.id = trackedElement.id;
      }
    });

    console.log(sectionElements)
  }

  handleSelectConsequenceRegion(consequenceMap: ConsequenceMapRegion) {
    // If user selected the default option, delete the database entry
    if (consequenceMap.consequenceId === this.defaultRegionSelectValue) {
      if (consequenceMap.id) {
        // Delete existing entry from database
        this.consequenceMapService.deleteConsequenceMapRegion(consequenceMap.id).subscribe({
          next: () => {
            console.log("Deleted region consequence mapping, will use default");
            consequenceMap.id = undefined; // Clear the ID since it's deleted
          },
          error: (error) => {
            console.error("Error deleting region consequence mapping:", error);
          }
        });
      }
      return;
    }

    // Save/update the actual consequence selection
    const dto = toConsequenceMapRegionDTO(consequenceMap)
    this.consequenceMapService.postConsequenceMapRegion(dto).subscribe(x => {
      console.log("inserted/updated region consequence id: ", x.id)
      consequenceMap.id = x.id; // Store the database ID
    })
    console.log(consequenceMap)
  }

  handleSelectConsequenceSection(consequenceMap: ConsequenceMapSection) {
    // If user selected the default option (inherit from region), delete the database entry
    if (consequenceMap.consequenceId === this.defaultSectionSelectValue) {
      if (consequenceMap.id) {
        // Delete existing entry from database
        this.consequenceMapService.deleteConsequenceMapSection(consequenceMap.id).subscribe({
          next: () => {
            console.log("Deleted section consequence mapping, will inherit from region");
            consequenceMap.id = undefined; // Clear the ID since it's deleted
          },
          error: (error) => {
            console.error("Error deleting section consequence mapping:", error);
          }
        });
      }
      return;
    }

    // Save/update the actual consequence selection
    const dto = toConsequenceMapSectionDTO(consequenceMap)
    this.consequenceMapService.postConsequenceMapSection(dto).subscribe(x => {
      console.log("inserted/updated section consequence id: ", x.id)
      consequenceMap.id = x.id; // Store the database ID
    })
    console.log(consequenceMap)
  }

  handleSelectConsequenceElement(consequenceMap: ConsequenceMapElement) {
    // If user selected the default option (inherit from section), delete the database entry
    if (consequenceMap.consequenceId === this.defaultElementSelectValue) {
      if (consequenceMap.id) {
        // Delete existing entry from database
        this.consequenceMapService.deleteConsequenceMapElement(consequenceMap.id).subscribe({
          next: () => {
            console.log("Deleted element consequence mapping, will inherit from section");
            consequenceMap.id = undefined; // Clear the ID since it's deleted
            // Remove from tracked elements
            this.trackedElements = this.trackedElements.filter(el => 
              !(el.elementId === consequenceMap.elementId && 
                el.sectionId === consequenceMap.sectionId && 
                el.regionId === consequenceMap.regionId)
            );
          },
          error: (error) => {
            console.error("Error deleting element consequence mapping:", error);
          }
        });
      }
      return;
    }

    // Save/update the actual consequence selection
    const dto = toConsequenceMapElementDTO(consequenceMap)
    this.consequenceMapService.postConsequenceMapElement(dto).subscribe(x => {
      console.log("inserted/updated element consequence id: ", x.id)
      consequenceMap.id = x.id; // Store the database ID
      this.updateTrackedElementsStore(consequenceMap)
    })
    console.log(consequenceMap)
  }

  fillTrackedElementsStore(elements: ConsequenceMapElementDTO[]) {
    for(let element of elements) {
      const tElement: ConsequenceMapElement = {
        assetId: element.assetId,
        regionId: element.regionId,
        sectionId: element.sectionId,
        elementId: element.elementId,
        name: '',
        consequenceId: element.consequenceId,
        id: element.id,
        visible: false
      }

      this.trackedElements.push(tElement)
    }
  }

  updateTrackedElementsStore(element: ConsequenceMapElement) {
    const index = this.trackedElements.findIndex(el => el.elementId === element.elementId);

    if (index > -1) {
        this.trackedElements[index] = {...this.trackedElements[index], ...element};
    } else {
        this.trackedElements.push(element);
    }
  }

  getConsequenceName(consequenceId: string): string {
    if (!consequenceId || !this.consequenceItems) {
      return '';
    }
    
    const consequence = this.consequenceItems.find(item => item.id === consequenceId);
    return consequence ? consequence.name : '';
  }

  closeWindow(): void {
    this.templateService.toggleConsequenceMap(false);
  }

  buildSectionOnlyView(): void {
    const sectionMap = new Map<string, SectionOnlyViewItem>();

    this.regions.forEach(region => {
      region.sections.forEach(section => {
        const key = `${section.sectionId}-${section.name}`;
        
        if (!sectionMap.has(key)) {
          sectionMap.set(key, {
            sectionId: section.sectionId,
            name: section.name,
            assetId: section.assetId,
            instances: [],
            effectiveConsequenceId: null,
            hasMultipleConsequences: false,
            multiConsequenceDetails: [],
            consequenceAnalysis: {
              majorityInfo: null,
              differentRegions: []
            }
          });
        }

        const sectionItem = sectionMap.get(key)!;
        sectionItem.instances.push({
          regionId: region.regionId,
          regionName: region.name,
          section: section
        });
      });
    });

    // Convert map to array and analyze consequences
    this.sectionOnlyView = Array.from(sectionMap.values()).map(item => {
      this.analyzeSectionConsequences(item);
      return item;
    });

    // Update filtered arrays
    this.updateFilteredData();
    this.cdr.detectChanges();
  }

  analyzeSectionConsequences(sectionItem: SectionOnlyViewItem): void {
    const consequences = new Set<string>();
    const details: { regionId: string; regionName: string; consequenceId: string; consequenceName: string }[] = [];

    sectionItem.instances.forEach(instance => {
      const region = this.regions.find(r => r.regionId === instance.regionId)!;
      let effectiveConsequence: string;
      
      // Get effective consequence following inheritance chain: Section -> Region -> First consequence item
      if (instance.section.consequenceId && instance.section.consequenceId !== this.defaultSectionSelectValue) {
        effectiveConsequence = instance.section.consequenceId;
      } else if (region.consequenceId && region.consequenceId !== this.defaultRegionSelectValue) {
        effectiveConsequence = region.consequenceId;
      } else {
        effectiveConsequence = this.consequenceItems && this.consequenceItems.length > 0 ? this.consequenceItems[0].id : null;
      }
      
      consequences.add(effectiveConsequence);
      details.push({
        regionId: instance.regionId,
        regionName: instance.regionName,
        consequenceId: effectiveConsequence,
        consequenceName: this.getConsequenceName(effectiveConsequence)
      });
    });

    sectionItem.hasMultipleConsequences = consequences.size > 1;
    sectionItem.multiConsequenceDetails = details;
    
    if (consequences.size === 1) {
      sectionItem.effectiveConsequenceId = Array.from(consequences)[0];
    } else {
      sectionItem.effectiveConsequenceId = "MULTI_CONSEQUENCE";
    }

    // Compute consequence analysis
    if (sectionItem.hasMultipleConsequences) {
      // Group by consequence and find the most common one
      const consequenceGroups = new Map<string, MultiConsequenceDetail[]>();
      
      sectionItem.multiConsequenceDetails.forEach(detail => {
        if (!consequenceGroups.has(detail.consequenceId)) {
          consequenceGroups.set(detail.consequenceId, []);
        }
        consequenceGroups.get(detail.consequenceId)!.push(detail);
      });

      // Find the most common consequence (the majority)
      let majorityConsequence = Array.from(consequenceGroups.values())[0][0].consequenceId;
      let majorityCount = consequenceGroups.get(majorityConsequence)!.length;
      let majorityConsequenceName = Array.from(consequenceGroups.values())[0][0].consequenceName;

      consequenceGroups.forEach((details, consequenceId) => {
        if (details.length > majorityCount) {
          majorityConsequence = consequenceId;
          majorityCount = details.length;
          majorityConsequenceName = details[0].consequenceName;
        }
      });

      // Get regions that are different from the majority
      const differentRegions: MultiConsequenceDetail[] = [];
      consequenceGroups.forEach((details, consequenceId) => {
        if (consequenceId !== majorityConsequence) {
          differentRegions.push(...details);
        }
      });

      sectionItem.consequenceAnalysis = {
        majorityInfo: {
          consequenceName: majorityConsequenceName,
          count: majorityCount
        },
        differentRegions
      };
    } else {
      sectionItem.consequenceAnalysis = {
        majorityInfo: null,
        differentRegions: []
      };
    }
  }

  toggleSectionMode(isSectionMode: boolean): void {
    this.isSectionMode = isSectionMode;
    if (this.isSectionMode) {
      this.buildSectionOnlyView();
    }
    // Update filtered data when switching modes
    this.updateFilteredData();
    // Clear current selection when switching modes
    this.currentSelectedItem = null;
    this.cdr.detectChanges();
  }

  handleSelectConsequenceSectionMode(sectionItem: SectionOnlyViewItem, consequenceId: string): void {
    // If user selected the multi-consequence option, do nothing (it's just a display option)
    if (consequenceId === 'MULTI_CONSEQUENCE') {
      return;
    }

    const isDefault = consequenceId === this.defaultSectionSelectValue;
    const updatePromises: Promise<void>[] = [];

    console.log(`Updating consequence for section "${sectionItem.name}" across ${sectionItem.instances.length} regions to: ${isDefault ? 'default' : this.getConsequenceName(consequenceId)}`);

    sectionItem.instances.forEach(instance => {
      const section = instance.section;
      
      if (isDefault) {
        // Delete existing entries for this section instance
        if (section.id) {
          const deletePromise = new Promise<void>((resolve, reject) => {
            this.consequenceMapService.deleteConsequenceMapSection(section.id!).subscribe({
              next: () => {
                console.log(`Deleted section consequence mapping for ${section.name} in region ${instance.regionName}`);
                section.id = undefined;
                section.consequenceId = this.defaultSectionSelectValue;
                resolve();
              },
              error: (error) => {
                console.error("Error deleting section consequence mapping:", error);
                reject(error);
              }
            });
          });
          updatePromises.push(deletePromise);
        } else {
          section.consequenceId = this.defaultSectionSelectValue;
        }
      } else {
        // Set consequence for this section instance
        section.consequenceId = consequenceId;
        const dto = toConsequenceMapSectionDTO(section);
        
        const updatePromise = new Promise<void>((resolve, reject) => {
          this.consequenceMapService.postConsequenceMapSection(dto).subscribe({
            next: (response) => {
              console.log(`Updated section consequence for ${section.name} in region ${instance.regionName}:`, response.id);
              section.id = response.id;
              resolve();
            },
            error: (error) => {
              console.error("Error updating section consequence mapping:", error);
              reject(error);
            }
          });
        });
        updatePromises.push(updatePromise);
      }
    });

    // Wait for all updates to complete, then refresh the section analysis
    Promise.all(updatePromises).then(() => {
      this.analyzeSectionConsequences(sectionItem);
      console.log(`Completed bulk update for section: ${sectionItem.name}`);
    }).catch((error) => {
      console.error(`Error during bulk update for section ${sectionItem.name}:`, error);
      // Still refresh the analysis to show current state
      this.analyzeSectionConsequences(sectionItem);
    });
  }

  
  handleClickSectionMode(sectionItem: SectionOnlyViewItem): void {
    // Toggle expansion - if already expanded, collapse; otherwise expand
    if (this.expandedSectionId === sectionItem.sectionId) {
      this.expandedSectionId = null;
    } else {
      this.expandedSectionId = sectionItem.sectionId;
    }
  }

  // Search functionality methods
  onSearchChange(): void {
    this.updateFilteredData();
  }

  updateFilteredData(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredRegions = [...this.regions];
      this.filteredSectionOnlyView = [...this.sectionOnlyView];
    } else {
      const searchTermLower = this.searchTerm.toLowerCase().trim();
      
      // Filter regions by name
      this.filteredRegions = this.regions.filter(region =>
        region.name.toLowerCase().includes(searchTermLower)
      );

      // Filter sections by name
      this.filteredSectionOnlyView = this.sectionOnlyView.filter(section =>
        section.name.toLowerCase().includes(searchTermLower)
      );
    }
  }
}
